import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  CheckCircle, 
  ExternalLink,
  Clock,
  AlertCircle
} from "lucide-react";
import { mockData } from "../mock/mockData";
import { sendContactEmail } from "../services/emailService";

const ContactSection = () => {
  const { contact } = mockData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Enviar email usando EmailJS
      const result = await sendContactEmail(formData);
      
      if (result.success) {
        console.log('Email enviado exitosamente');
        setIsSubmitted(true);
        
        // Reset form después de 4 segundos
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            message: ''
          });
        }, 4000);
      } else {
        setError(result.message || 'Error al enviar el mensaje');
      }

    } catch (error) {
      console.error('Error en handleSubmit:', error);
      setError('Error inesperado. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contacto" className="py-20 bg-gradient-to-br from-[#3E97FF]/5 to-[#27AE60]/5">
        <div className="container mx-auto px-6">
          <div className="max-w-md mx-auto text-center">
            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8">
                <CheckCircle className="w-16 h-16 text-[#27AE60] mx-auto mb-6 animate-bounce" />
                <h3 className="text-2xl font-bold text-[#252F4A] mb-4">¡Mensaje Enviado!</h3>
                <p className="text-[#252F4A]/70 mb-4">
                  Gracias por contactarnos. Hemos recibido tu mensaje y nos pondremos 
                  en contacto contigo dentro de las próximas 24 horas.
                </p>
                <p className="text-sm text-[#252F4A]/60">
                  También hemos enviado una confirmación a tu correo electrónico.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contacto" className="py-20 bg-gradient-to-br from-[#3E97FF]/5 to-[#27AE60]/5">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#252F4A] mb-6">
            Contáctanos
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#3E97FF] to-[#27AE60] mx-auto mb-6"></div>
          <p className="text-lg text-[#252F4A]/70 max-w-2xl mx-auto">
            Estamos aquí para ayudarte. Cuéntanos sobre tu proyecto y 
            descubre cómo podemos impulsar el crecimiento de tu empresa.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-[#252F4A] flex items-center gap-3">
                <Send className="w-6 h-6 text-[#3E97FF]" />
                Envíanos un mensaje
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#252F4A] font-medium">
                      Nombre completo *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="border-[#3E97FF]/20 focus:border-[#3E97FF] focus:ring-[#3E97FF]"
                      placeholder="Tu nombre completo"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#252F4A] font-medium">
                      Correo electrónico *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="border-[#3E97FF]/20 focus:border-[#3E97FF] focus:ring-[#3E97FF]"
                      placeholder="tu@email.com"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-[#252F4A] font-medium">
                    Empresa (opcional)
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="border-[#3E97FF]/20 focus:border-[#3E97FF] focus:ring-[#3E97FF]"
                    placeholder="Nombre de tu empresa"
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#252F4A] font-medium">
                    Mensaje *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="border-[#3E97FF]/20 focus:border-[#3E97FF] focus:ring-[#3E97FF] resize-none"
                    placeholder="Cuéntanos sobre tu proyecto, necesidades o consulta..."
                    disabled={isLoading}
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#3E97FF] hover:bg-[#1C3C73] disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Enviando mensaje...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar mensaje
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Details */}
            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#252F4A]">
                  Información de contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-[#3E97FF] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#252F4A]">Correo electrónico</h4>
                    <p className="text-[#252F4A]/70">{contact.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-[#070707] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#252F4A]">Teléfono</h4>
                    <p className="text-[#252F4A]/70">{contact.phone}</p>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start space-x-4">
                  <svg className="w-6 h-6 text-[#27AE60]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <div>
                    <h4 className="font-semibold text-[#252F4A]">WhatsApp</h4>
                    <p className="text-[#252F4A]/70">
                      <a 
                        href={`https://wa.me/${contact.phone.replace(/\D/g, '')}?text=${encodeURIComponent('Quiero más información en Infinitux Solutions!')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#27AE60] hover:underline"
                      >
                        {contact.phone}
                      </a>
                    </p>
                  </div>
                </div>

                {/* <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-[#FF6B35] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#252F4A]">Dirección</h4>
                    <p className="text-[#252F4A]/70">{contact.address}</p>
                  </div>
                </div> */}

                <div className="flex items-start space-x-4">
                  <Clock className="w-6 h-6 text-[#1C3C73] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#252F4A]">Horario de atención</h4>
                    <p className="text-[#252F4A]/70">
                      Lunes a Viernes: 8:00 AM - 6:00 PM<br />
                      Sábados: 9:00 AM - 2:00 PM
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            {/* <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#252F4A]">
                  Síguenos en redes sociales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4">
                  <a 
                    href={contact.socialMedia.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#0077B5] hover:bg-[#005E96] text-white rounded-lg flex items-center justify-center transition-colors"
                  >
                    <ExternalLink className="w-6 h-6" />
                  </a>
                  <a 
                    href={contact.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg flex items-center justify-center transition-colors"
                  >
                    <ExternalLink className="w-6 h-6" />
                  </a>
                  <a 
                    href={contact.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-r from-[#E4405F] to-[#833AB4] hover:from-[#D73653] hover:to-[#7530A0] text-white rounded-lg flex items-center justify-center transition-all"
                  >
                    <ExternalLink className="w-6 h-6" />
                  </a>
                </div>
              </CardContent>
            </Card> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;